import { reactive, ref, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useForm, useField } from 'vee-validate'
import { object, string, date, array } from 'yup'
import { usePoliticalActions } from './usePoliticalActions'
import { usePoliticalActionDialog } from './usePoliticalActionDialog'
import { useNotifications } from '@/services/notification/useNotifications'
import { useTopData } from '@/services/useHome'
import { useLogs } from '@/services/useLogs'
import { useLoader } from '../useLoader'
import { useError } from '../useError'

// import { useImgUploader } from '@/services/useImgUploader'
import axios from '@/axios'

const tabs = reactive([
  {
    name: '基本情報',
    required: true,
    status: 0,
    fields: [
      'submissionDate',
      'selectedActionTypes',
      'startDate',
      'endDate',
      'memberName',
    ],
  },
  {
    name: '場所·目的',
    required: true,
    status: 0,
    fields: ['place', 'purpose'],
  },
  { name: '備考', required: false, status: 0, fields: ['remarks'] },
  { name: '資料添付', required: false, status: 0, addFilesBtn: false },
])

const actionTypes = ref([
  { id: 1, name: '調査研究', hasAttribute: false },
  { id: 2, name: '研修', hasAttribute: false },
  { id: 3, name: '要望·陳情', hasAttribute: false },
])

export const useRequestFormTabs = () => {
  return { tabs }
}

const schema = object({
  submissionDate: date()
    .required('必須です')
    .typeError('必須です'),
  selectedActionTypes: array()
    .min(1)
    .required('必須です'),
  startDate: date()
    .required('必須です')
    .typeError('必須です'),
  endDate: date()
    .typeError('必須です')
    .required('必須です')
    .when(
      'startDate',
      (startDate, schema) =>
        startDate && schema.min(startDate, '日程が前後しています'),
    ),
  memberName: string()
    .required('必須です')
    .typeError('参加者の氏名を入力してください'),
  place: string()
    .required('必須です')
    .typeError('場所を入力してください'),
  purpose: string()
    .required('必須です')
    .typeError('目的を入力してください'),
  remarks: string().nullable(),
  files: array(),
})

export const useRequestForm = () => {
  const getBitActionType = array => {
    let bit = 0
    // 1: 調査研究 2: 研修 4: 要望・陳情
    if (array.length > 0) {
      array.forEach(item => {
        if (item.name.includes('調査研究') && item.hasAttribute == true) {
          bit = bit + 1
        }
        if (item.name.includes('研修') && item.hasAttribute == true) {
          bit = bit + 2
        }
        if (item.name.includes('要望·陳情') && item.hasAttribute == true) {
          bit = bit + 4
        }
      })
      return [bit]
    }
    return []
  }

  const isFirstTabFilled = () => {
    if (
      currentPoliticalAction.submissionDate != null &&
      currentPoliticalAction.startDate != null &&
      currentPoliticalAction.endDate != null &&
      currentPoliticalAction.memberName != null &&
      currentPoliticalAction.memberName.length > 0 &&
      getBitActionType(currentPoliticalAction.selectedActionTypes) > 0
    ) {
      return true
    } else {
      return false
    }
  }

  const { getNotificationsAfterResponse } = useNotifications()
  const { getTimelineAfterResponse } = useLogs()
  const { getTopDataAfterResponse } = useTopData()
  const {
    currentPoliticalAction,
    getActivitiesAfterResponse,
  } = usePoliticalActions()
  const { activityDialog } = usePoliticalActionDialog()
  const { toggleShowLoader } = useLoader()
  const { toggleShowError } = useError()
  const route = useRoute()

  watch(activityDialog, () => {
    if (activityDialog.isPreview) {
      currentPoliticalAction.submissionDate = submissionDate.value
      currentPoliticalAction.startDate = startDate.value
      currentPoliticalAction.endDate = endDate.value
      currentPoliticalAction.memberName = memberName.value
      currentPoliticalAction.actionTypes = [...selectedActionTypes.value]
      currentPoliticalAction.place = place.value
      currentPoliticalAction.purpose = purpose.value
      currentPoliticalAction.remarks = remarks.value
    }
  })

  onMounted(() => {
    for (const x of Array(4).keys()) {
      setTabStatus(x, 0)
    }
    if (currentPoliticalAction.id > 0) validate()
  })

  const { meta, handleSubmit } = useForm({ validationSchema: schema })

  watch(meta, meta => {
    // console.log("meta in useRequestForm", meta)
    tabs[1].required =
      currentPoliticalAction.purpose &&
      currentPoliticalAction.purpose.length > 0 &&
      currentPoliticalAction.place &&
      currentPoliticalAction.place.length > 0
    if (isFirstTabFilled()) {
      tabs[0].required = true
    } else {
      tabs[0].required = false
    }

    if (meta.valid) {
      activityDialog.isPreviewEnabled = true
    } else {
      activityDialog.isPreviewEnabled = false
    }
  })

  const { value: submissionDate, errorMessage: submissionDateError } = useField(
    'submissionDate',
    undefined,
    {
      initialValue: currentPoliticalAction.submissionDate
        ? new Date(currentPoliticalAction.submissionDate)
        : undefined,
    },
  )

  const {
    value: selectedActionTypes,
    errorMessage: selectedActionTypesError,
  } = useField('selectedActionTypes', undefined, {
    initialValue: currentPoliticalAction.selectedActionTypes || new Array(),
  })

  const { value: startDate, errorMessage: startDateError } = useField(
    'startDate',
    undefined,
    {
      initialValue: currentPoliticalAction.startDate
        ? new Date(currentPoliticalAction.startDate)
        : undefined,
    },
  )

  const { value: endDate, errorMessage: endDateError } = useField(
    'endDate',
    undefined,
    {
      initialValue: currentPoliticalAction.endDate
        ? new Date(currentPoliticalAction.endDate)
        : undefined,
    },
  )

  const { value: memberName, errorMessage: memberNameError } = useField(
    'memberName',
    undefined,
    {
      initialValue: currentPoliticalAction.memberName,
    },
  )

  const { value: place, errorMessage: placeError } = useField(
    'place',
    undefined,
    { initialValue: currentPoliticalAction.place },
  )

  const { value: purpose, errorMessage: purposeError } = useField(
    'purpose',
    undefined,
    { initialValue: currentPoliticalAction.purpose },
  )

  const { value: remarks, errorMessage: remarksError } = useField(
    'remarks',
    undefined,
    { initialValue: currentPoliticalAction.application.remarks },
  )

  const { value: files, errorMessage: filesError } = useField(
    'files',
    undefined,
    { initialValue: currentPoliticalAction.application.images },
  )

  function onInvalidSubmit({ results, errors }) {
    // set ok status for all tabs
    for (const x of Array(4).keys()) {
      setTabStatus(x, 1)
    }

    // TODO: いるのかいらないのかわからない、調査必要
    console.debug(results)
    const errorFields = Object.keys(errors)
    errorFields.forEach(field => {
      const tabIndex = findField(field)
      // set validation error status for tab by tabindex
      if (tabIndex > -1) setTabStatus(tabIndex, -1)
    })

    if (currentPoliticalAction.sentBack) {
      currentPoliticalAction.sentBack.forEach(item => {
        const tabIndex = findField(item.targetItem)
        if (tabIndex > -1) setTabStatus(tabIndex, -10)
      })
    }
  }

  const validate = handleSubmit(() => {
    for (const x of Array(4).keys()) {
      setTabStatus(x, 1)
    }
  }, onInvalidSubmit)

  // TODO: いらないかもしれない要調査
  const onSubmit = handleSubmit(values => {
    // set ok status for all tabs
    for (const x of Array(4).keys()) {
      setTabStatus(x, 1)
    }
    alert(JSON.stringify(values, null, 2))
  }, onInvalidSubmit)

  const findField = fieldName => {
    for (let i = 0, len = tabs.length; i < len; i++) {
      if (tabs[i].fields && tabs[i].fields.find(field => field == fieldName)) {
        return i
      }
    }
    return -1
  }

  const setTabStatus = (tabIndex, status) => {
    tabs[tabIndex].status = status
  }

  const setTabRequired = (tabIndex, isRequired) => {
    tabs[tabIndex].required = isRequired
  }

  watch(remarks, remarks => {
    if (remarks.length) {
      setTabRequired(2, true)
    } else {
      setTabRequired(2, false)
    }
  })

  const handleImages = files => {
    if (files.length > 0) {
      tabs[3].required = true
      tabs[3].addFilesBtn = true
    } else {
      tabs[3].required = false
      tabs[3].addFilesBtn = false
    }
  }

  const getSentBackItemMessage = attr => {
    const sentBackList = []
    if (
      currentPoliticalAction.application.option.viewType === 0 ||
      currentPoliticalAction.application.option.viewType === 1 ||
      currentPoliticalAction.application.sentBackItems.length == 0
    ) {
      return []
    }
    currentPoliticalAction.application.sentBackItems.forEach(item => {
      if (!item || item.length == 0) return []
      const backItem = item.items.find(item => item.target == attr)
      if (backItem !== undefined) {
        sentBackList.push(backItem)
      }
    })
    if (sentBackList.length > 0) {
      sentBackList
      return sentBackList
    }
    return []
  }

  watch(memberName, () => {
    currentPoliticalAction.memberName = memberName.value
  })
  watch(startDate, () => {
    currentPoliticalAction.startDate = startDate.value
  })
  watch(endDate, () => {
    currentPoliticalAction.endDate = endDate.value
  })
  watch(submissionDate, () => {
    currentPoliticalAction.submissionDate = submissionDate.value
  })
  watch(place, () => {
    currentPoliticalAction.place = place.value
  })
  watch(purpose, () => {
    currentPoliticalAction.purpose = purpose.value
  })
  watch(remarks, () => {
    currentPoliticalAction.remarks = remarks.value
  })

  const submitActivity = async (id, type) => {
    try {
      toggleShowLoader(true)
      if (!(type === 'application' || type === 'application-apply')) {
        throw new Error('invalid type: ' + type)
      }
      const payload = {
        is_temporary: type === 'application',
        document_type: currentPoliticalAction.application.documentType,
        action_types: getBitActionType(
          currentPoliticalAction.selectedActionTypes,
        ),
        start_date: currentPoliticalAction.startDate,
        end_date: currentPoliticalAction.endDate,
        place: currentPoliticalAction.place,
        member_name: currentPoliticalAction.memberName,
        purpose: currentPoliticalAction.purpose,
        remarks: currentPoliticalAction.remarks,
        submission_date: currentPoliticalAction.submissionDate,
        images: currentPoliticalAction.application.images,
      }
      const isPost = id === null
      const response = isPost
        ? await axios.post(`/actions`, payload)
        : await axios.put(`/actions/${id}`, payload)
      await getActivitiesAfterResponse(response)
      if (route.name === 'Top') {
        await getTopDataAfterResponse(response)
      } else if (route.name === 'Logs') {
        await getTimelineAfterResponse(response)
      } else if (route.name === 'Notifications') {
        await getNotificationsAfterResponse(response)
      }
      // console.log(this.$route.path)

      toggleShowLoader(false)
    } catch (e) {
      toggleShowError(true)
      toggleShowLoader(false)
    }
  }

  const cancelRequest = async (id, type) => {
    try {
      toggleShowLoader(true)
      const res = await axios.post(`/actions/cancel`, {
        document_id: id,
        document_type: type,
      })
      await getActivitiesAfterResponse(res)
      if (route.name === 'Top') {
        await getTopDataAfterResponse(res)
      } else if (route.name === 'Logs') {
        await getTimelineAfterResponse(res)
      } else if (route.name === 'Notifications') {
        await getNotificationsAfterResponse(res)
      }

      toggleShowLoader(false)
    } catch (e) {
      toggleShowError(true)
      toggleShowLoader(false)
    }
  }

  const approveRequest = async (id, type) => {
    try {
      toggleShowLoader(true)
      const res = await axios.post(`/actions/approve`, {
        document_id: id,
        document_type: type,
      })
      await getActivitiesAfterResponse(res)
      toggleShowLoader(false)
    } catch (e) {
      toggleShowError(true)
      toggleShowLoader(false)
    }
  }

  const resetActionTypes = () => {
    actionTypes.value.forEach(item => {
      item.hasAttribute = false
    })
  }
  return {
    setTabStatus,
    findField,
    onSubmit,
    handleImages,
    getSentBackItemMessage,
    submitActivity,
    cancelRequest,
    approveRequest,
    resetActionTypes,
    meta,
    actionTypes,
    submissionDate,
    submissionDateError,
    selectedActionTypes,
    selectedActionTypesError,
    startDate,
    startDateError,
    endDate,
    endDateError,
    memberName,
    memberNameError,
    place,
    placeError,
    purpose,
    purposeError,
    remarks,
    remarksError,
    files,
    filesError,
  }
}
